import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tooltip,
  Stack,
  Avatar,
  IconButton,
} from "@mui/material";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import { useUsuario } from "../../../Hooks/UsuarioHook";
import {
  resolverSistemaDisplay,
  iniciaisSistema,
  hueSistema,
  urlAbsolutaDoSistema,
} from "../../../Utils/sistemaDisplay";
import { logoDoFront } from "../../../Utils/iconesSistema";
import { metadadosCategoria, resolverCategoria } from "../../../Utils/categoriasSistema";
import { UsuariosDoSistemaModal } from "../UsuariosDoSistemaModal";

interface Sistema {
  id: number;
  titulo: string;
  descricao: string;
  logo: string;
  url: string;
  /**
   * A lista de sistemas do usuário é heterogênea (ver getMeusSistemas no backend):
   * os liberados AO USUÁRIO vêm de SISTEMA_USUARIO e trazem `id` = id da ASSOCIAÇÃO
   * + `sistema_id` = id real; os liberados PARA TODOS vêm da tabela SISTEMA e o
   * `id` já é o real. Por isso sempre use `sistema_id ?? id`.
   */
  sistema_id?: number;
  /** true nos sistemas liberados para TODOS (não têm vínculo individual em SISTEMA_USUARIO) */
  liberado?: boolean | number;
}

export const SistemaCard = ({ sistema }: { sistema: Sistema }) => {
  const { usuario } = useUsuario();
  // Boolean(): `administrador` vem do MySQL como tinyint (0/1). Sem isso,
  // `0 && <IconButton/>` renderiza o literal 0 no card do não-administrador.
  const isAdmin = Boolean(usuario?.usuario?.administrador);
  const [verUsuarios, setVerUsuarios] = useState(false);

  // id REAL do sistema (ver comentário na interface Sistema)
  const sistemaId = sistema.sistema_id ?? sistema.id;

  // Sistema liberado para TODOS não tem lista de acesso por usuário — o modal não
  // faria sentido ali. O Cidade Digital (e seus rebrands: TRESPADIGITAL, meuMax,
  // Trindade na Palma da Mão) é checado pelo título porque, quando o usuário tem
  // um vínculo avulso em SISTEMA_USUARIO, o item vem por esse caminho e chega
  // aqui SEM o campo `liberado`.
  const liberadoParaTodos =
    Boolean(sistema.liberado) || sistema.titulo === "Cidade Digital";
  const podeVerUsuarios = isAdmin && !liberadoParaTodos;

  // título + logo (aplica a regra especial do Cidade Digital)
  const { titulo, logoSrc: logoDoBanco } = resolverSistemaDisplay(
    sistema,
    usuario.entidade?.banco
  );

  // PREVIEW: logo padronizada pelo front (ver Utils/iconesSistema) tem
  // prioridade sobre a do banco. `sistema` e não `titulo`: o mapa usa o
  // título CRU do banco.
  const logoSrc = logoDoFront(sistema) ?? logoDoBanco;

  // Identidade visual do fallback: sistema sem logo cadastrada ganha as iniciais
  // numa cor derivada do próprio título, em vez do mesmo ícone genérico pra todos.
  const iniciais = iniciaisSistema(titulo);
  const hueIniciais = hueSistema(titulo);

  // Filete e hover na cor da SEÇÃO: a cor sorteada por título deixava cada card
  // de uma cor e a seção parecia um arco-íris sem lógica.
  const hue = metadadosCategoria(resolverCategoria(sistema)).hue;

  const descricao = String(sistema.descricao ?? "").trim();
  const mostrarDescricao =
    descricao !== "" && descricao.toLowerCase() !== titulo.trim().toLowerCase();

  // urlAbsolutaDoSistema: garante o "https://" — sem ele o href vira relativo
  // e o card abre ".../login-unico/solucoes.sinsoft.com.br/..." (link quebrado)
  const href = urlAbsolutaDoSistema(
    sistema.url.includes("solucoes.sinsoft.com.br/iss")
      ? `${sistema.url}/${usuario.entidade.banco}`
      : sistema.url
  );

  return (
    // O card inteiro é um link, então o botão do modal fica FORA da âncora
    // (button dentro de <a> é HTML inválido) e é posicionado por cima.
    <Box
      sx={{
        position: "relative",
        display: "flex",
        width: "100%",
        // o card sobe 4px no hover — o botão acompanha para não descolar do canto
        "&:hover .acao-usuarios-sistema": { transform: "translateY(-4px)" },
      }}
    >
      <Card
        component="a"
        href={href}
        sx={{
          textDecoration: "none",
          borderRadius: 3,
          bgcolor: "background.paper",
          border: 1,
          borderColor: "divider",
          boxShadow: "none",
          cursor: "pointer",
          overflow: "hidden",
          position: "relative",
          width: "100%",
          // Altura FIXA, igual para todos os cards da home — com "100%" cada
          // linha tinha a altura do seu card mais alto (título em 2 linhas, sem
          // descrição...) e as seções saíam desalinhadas entre si. 96px cabe o
          // pior caso: ícone + título em 2 linhas.
          height: 96,
          transition:
            "transform .18s ease, box-shadow .22s ease, border-color .22s ease",
          // Filete colorido no topo: dá identidade ao card e reforça, de relance,
          // que sistemas diferentes são coisas diferentes.
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            bgcolor: `hsl(${hue} 70% 55%)`,
            opacity: 0.85,
            transition: "opacity .22s ease",
          },
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: (t: any) =>
              t.palette.mode === "dark"
                ? "0 12px 28px rgba(0,0,0,0.5)"
                : `0 12px 26px hsla(${hue}, 60%, 45%, 0.22)`,
            borderColor: `hsl(${hue} 60% 55%)`,
          },
          "&:hover::before": { opacity: 1 },
        }}
      >
        <CardContent
          sx={{
            p: 1.25,
            "&:last-child": { pb: 1.25 },
            height: "100%",
            boxSizing: "border-box",
            // conteúdo centralizado na altura fixa: card com pouco texto não
            // fica com um buraco embaixo
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Stack spacing={0.6} alignItems="center" textAlign="center">
            {/* logo do front > logo do banco > iniciais coloridas */}
            <Avatar
              variant="rounded"
              src={logoSrc || undefined}
              alt={titulo}
              sx={{
                width: 34,
                height: 34,
                // Com logo: sem canto arredondado e sem recorte — o Avatar corta
                // o que passa do raio, e as pontas do desenho (telhado, chaminé,
                // árvore) sumiam. O canto arredondado fica só para as iniciais.
                borderRadius: logoSrc ? 0 : 2,
                overflow: logoSrc ? "visible" : "hidden",
                fontSize: "0.85rem",
                fontWeight: 800,
                letterSpacing: 0.5,
                // Fundo pastel só para as INICIAIS: as logos já são coloridas, e
                // a bolinha atrás delas era mais uma cor solta no card.
                bgcolor: (t: any) =>
                  logoSrc
                    ? "transparent"
                    : t.palette.mode === "dark"
                    ? `hsl(${hueIniciais} 42% 22%)`
                    : `hsl(${hueIniciais} 72% 94%)`,
                color: (t: any) =>
                  t.palette.mode === "dark"
                    ? `hsl(${hueIniciais} 70% 78%)`
                    : `hsl(${hueIniciais} 55% 32%)`,
                "& img": { objectFit: "contain" },
              }}
            >
              {iniciais}
            </Avatar>

            {/* Sem descrição no card (todos ficam só ícone + nome, padronizados):
                ela foi para o tooltip do nome, para quem não conhece o sistema.
                Quando a descrição só repete o título, o tooltip mostra o título. */}
            <Tooltip title={mostrarDescricao ? descricao : titulo} arrow placement="bottom">
              <Typography
                variant="body1"
                fontWeight={700}
                color="text.primary"
                sx={{
                  textDecoration: "none",
                  fontSize: "0.82rem",
                  lineHeight: 1.25,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {titulo}
              </Typography>
            </Tooltip>
          </Stack>
        </CardContent>

        {/* Sistema aberto a todos: vira um ícone no canto em vez de uma linha de
            texto — a informação continua ali (no tooltip) sem custar altura. */}
        {liberadoParaTodos && (
          <Tooltip title="Aberto a todos da entidade" arrow>
            <PublicRoundedIcon
              sx={{
                position: "absolute",
                top: 9,
                left: 8,
                fontSize: 14,
                color: "text.disabled",
              }}
            />
          </Tooltip>
        )}
      </Card>

      {/* Só administrador gerencia quem tem acesso ao sistema */}
      {podeVerUsuarios && (
        <Tooltip title="Gerenciar usuários com acesso" arrow>
          <IconButton
            size="small"
            className="acao-usuarios-sistema"
            aria-label={`Gerenciar usuários com acesso a ${titulo}`}
            onClick={() => setVerUsuarios(true)}
            sx={{
              position: "absolute",
              top: 8,
              right: 6,
              zIndex: 1,
              color: "text.disabled",
              transition: "transform .18s ease, color .2s, background-color .2s",
              "&:hover": { color: "primary.main", bgcolor: "action.hover" },
            }}
          >
            <GroupsRoundedIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      )}

      {verUsuarios && (
        <UsuariosDoSistemaModal
          sistemaId={sistemaId}
          titulo={titulo}
          onFechar={() => setVerUsuarios(false)}
        />
      )}
    </Box>
  );
};
